/**
 * Curve & measurement-data primitives.
 *
 * Everything here is authored sparsely (a handful of breakpoints) but is also
 * the exact shape produced by the measurement importers (spec 7, 43). A
 * modelled profile and a measured profile therefore land in the SAME structure
 * and the DSP compiler cannot tell them apart -- which is the whole point.
 */

import type { Confidence, Prov } from './provenance';

/** [frequency Hz, value dB] */
export type FRPoint = readonly [number, number];
/** [frequency Hz, magnitude ohms, phase degrees] */
export type ZPoint = readonly [number, number, number];
/** [frequency Hz, phase degrees] */
export type PhasePoint = readonly [number, number];

export type CurveInterp = 'log-linear' | 'log-pchip';

export interface FRCurve {
  kind: 'fr';
  /** Sparse breakpoints, ascending in frequency. dB relative to own reference. */
  points: FRPoint[];
  interp?: CurveInterp;
  /** dB offset applied to every point after interpolation. */
  offsetDb?: number;
  confidence?: Confidence;
  source?: string;
  /** Set when imported from a measurement file. */
  measurement?: MeasurementRef;
}

export interface PhaseCurve {
  kind: 'phase';
  /** Explicit phase breakpoints (degrees). */
  points?: PhasePoint[];
  /**
   * When no explicit phase data exists, declare how phase should be derived
   * from the magnitude response. This is honest: minimum-phase reconstruction
   * is a documented derivation, not a measurement.
   */
  derive?: 'minimum-phase' | 'linear-phase' | 'zero-phase';
  /** Pure delay in samples applied on top (excess phase). */
  excessDelaySamples?: number;
  confidence?: Confidence;
  source?: string;
}

/** A finite impulse response, either inline or referenced in IndexedDB. */
export interface IRData {
  kind: 'ir';
  sampleRate: number;
  /** One Float32Array per channel. 1 = mono, 2 = stereo, 4 = true-stereo matrix. */
  channels: Float32Array[];
  /** dB gain applied on load. */
  gainDb?: number;
  confidence?: Confidence;
  source?: string;
  measurement?: MeasurementRef;
}

/** Reference to an IR stored in IndexedDB rather than held in memory. */
export interface IRRef {
  kind: 'ir-ref';
  /** IndexedDB key. */
  id: string;
  sampleRate?: number;
  lengthSamples?: number;
  channels?: number;
  gainDb?: number;
  confidence?: Confidence;
  source?: string;
}

export type IRSource = IRData | IRRef;

export interface MeasurementRef {
  /** Free-form: 'Audio Precision APx555', 'Klippel NFS', 'REW + UMIK-1' ... */
  rig?: string;
  /** ISO date of the measurement. */
  date?: string;
  operator?: string;
  /** Conditions: load, level, temperature, gating, smoothing. */
  conditions?: string;
  /** Fractional-octave smoothing applied to the data (e.g. 6 -> 1/6 oct). */
  smoothingOct?: number;
  fileName?: string;
}

/**
 * Harmonic distortion structure.
 *
 * orders[n] is the level of the n-th harmonic in dB relative to the
 * fundamental, at refLevelDbFS. growthDbPerDb describes how that ratio changes
 * with drive level -- for a memoryless polynomial nonlinearity the n-th
 * harmonic ratio grows (n-1) dB per dB of input, which is the physical default
 * used when nothing better is known.
 */
export interface HarmonicSpectrum {
  kind: 'harmonics';
  refLevelDbFS: number;
  /** Sparse map: harmonic order -> dB relative to fundamental (negative). */
  orders: Record<number, number>;
  /** Per-order growth. Defaults to (n-1) dB/dB. */
  growthDbPerDb?: Record<number, number>;
  /** Frequency dependence of total distortion, dB offset vs frequency. */
  vsFrequency?: FRCurve;
  /** Fraction of H2 attributable to asymmetry (0..1). */
  asymmetry?: number;
  confidence?: Confidence;
  source?: string;
  measurement?: MeasurementRef;
}

export type NoiseShape = 'white' | 'pink' | 'thermal' | 'shaped';

export interface NoiseProfile {
  kind: 'noise';
  /** Broadband noise level, dBFS relative to full-scale output. */
  levelDbFS: number;
  shape: NoiseShape;
  /** For 'shaped': spectral shape in dB vs frequency. */
  curve?: FRCurve;
  /** Sigma-delta style HF noise shaping: dB/octave rise above shapeKneeHz. */
  shapingSlopeDbOct?: number;
  shapeKneeHz?: number;
  /** Correlation between channels: 0 = independent, 1 = identical. */
  channelCorrelation?: number;
  confidence?: Confidence;
  source?: string;
}

/** Stereo channel-domain behaviour. */
export interface ChannelBehaviour {
  /** L/R gain error in dB (positive = right louder). */
  imbalanceDb?: number;
  /** Crosstalk / separation in dB (negative, e.g. -110). */
  crosstalkDb?: number;
  /** Crosstalk rise with frequency, dB/decade (capacitive coupling -> +20). */
  crosstalkSlopeDbDec?: number;
  /** Interchannel delay error in samples. */
  skewSamples?: number;
}

/** Directivity / dispersion, authored as off-axis level offsets. */
export interface DirectivityData {
  kind: 'directivity';
  /** Angles in degrees for which curves exist (0 = on axis). */
  anglesDeg: number[];
  /** Horizontal off-axis response per angle (dB relative to on-axis). */
  horizontal: FRCurve[];
  /** Optional vertical set. */
  vertical?: FRCurve[];
  /** Derived: directivity index vs frequency. */
  di?: FRCurve;
  confidence?: Confidence;
  source?: string;
  measurement?: MeasurementRef;
}

// ---------------------------------------------------------------------------
// Curve evaluation helpers (used by the compiler and by the plot components)
// ---------------------------------------------------------------------------

/** Monotone cubic (PCHIP) / linear interpolation on a log-frequency axis. */
export function evalFR(curve: FRCurve | undefined | null, freqHz: number): number {
  if (!curve || !curve.points || curve.points.length === 0) return 0;
  const pts = curve.points;
  const off = curve.offsetDb ?? 0;
  const f = Math.max(1e-6, freqHz);
  if (pts.length === 1) return pts[0][1] + off;
  if (f <= pts[0][0]) return pts[0][1] + off;
  const last = pts[pts.length - 1];
  if (f >= last[0]) return last[1] + off;

  let lo = 0;
  let hi = pts.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (pts[mid][0] <= f) lo = mid;
    else hi = mid;
  }
  const i = lo;

  const x0 = Math.log(pts[i][0]);
  const x1 = Math.log(pts[i + 1][0]);
  const y0 = pts[i][1];
  const y1 = pts[i + 1][1];
  const x = Math.log(f);
  const span = x1 - x0 || 1;
  const t = (x - x0) / span;

  if (curve.interp === 'log-linear') return y0 + t * (y1 - y0) + off;

  // PCHIP: shape-preserving cubic Hermite. Needs neighbouring slopes.
  const slope = (a: number, b: number) => {
    const dx = Math.log(pts[b][0]) - Math.log(pts[a][0]);
    return dx === 0 ? 0 : (pts[b][1] - pts[a][1]) / dx;
  };
  const d1 = slope(i, i + 1);
  const dPrev = i > 0 ? slope(i - 1, i) : d1;
  const dNext = i + 2 < pts.length ? slope(i + 1, i + 2) : d1;
  const harmonic = (a: number, b: number) => (a * b <= 0 ? 0 : (2 * a * b) / (a + b));
  const m0 = i > 0 ? harmonic(dPrev, d1) : d1;
  const m1 = i + 2 < pts.length ? harmonic(d1, dNext) : d1;

  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return h00 * y0 + h10 * span * m0 + h01 * y1 + h11 * span * m1 + off;
}

export function evalImpedance(
  curve: ImpedanceCurve | undefined,
  freqHz: number,
): { mag: number; phase: number } {
  if (!curve || curve.points.length === 0) {
    return { mag: curve?.nominalOhms ?? 8, phase: 0 };
  }
  const pts = curve.points;
  const f = Math.max(1e-6, freqHz);
  if (f <= pts[0][0]) return { mag: pts[0][1], phase: pts[0][2] };
  const last = pts[pts.length - 1];
  if (f >= last[0]) return { mag: last[1], phase: last[2] };
  let lo = 0;
  let hi = pts.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (pts[mid][0] <= f) lo = mid;
    else hi = mid;
  }
  const x0 = Math.log(pts[lo][0]);
  const x1 = Math.log(pts[hi][0]);
  const t = (Math.log(f) - x0) / (x1 - x0 || 1);
  return {
    mag: pts[lo][1] + t * (pts[hi][1] - pts[lo][1]),
    phase: pts[lo][2] + t * (pts[hi][2] - pts[lo][2]),
  };
}

export interface ImpedanceCurve {
  kind: 'impedance';
  points: ZPoint[];
  /** Nominal impedance in ohms as marketed. */
  nominalOhms?: number;
  /** Minimum impedance -- often the number that actually loads the amplifier. */
  minOhms?: number;
  confidence?: Confidence;
  source?: string;
  measurement?: MeasurementRef;
}

/** Sample a curve onto a linear frequency grid of n bins up to nyquist. */
export function sampleFRLinear(
  curve: FRCurve | undefined,
  n: number,
  nyquist: number,
): Float32Array {
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) out[i] = evalFR(curve, (i / (n - 1)) * nyquist);
  return out;
}

export const flat = (): FRCurve => ({ kind: 'fr', points: [[20, 0], [20000, 0]] });

export function frCurve(
  points: FRPoint[],
  confidence: Confidence = 'MODELLED',
  source?: string,
): FRCurve {
  return { kind: 'fr', points, confidence, source };
}

export function impedanceCurve(
  points: ZPoint[],
  nominalOhms?: number,
  confidence: Confidence = 'MODELLED',
): ImpedanceCurve {
  return { kind: 'impedance', points, nominalOhms, confidence };
}

/** Combine two FR curves additively onto the union of their breakpoints. */
export function addFR(a: FRCurve | undefined, b: FRCurve | undefined): FRCurve {
  if (!a) return b ?? flat();
  if (!b) return a;
  const freqs = new Set<number>();
  for (const p of a.points) freqs.add(p[0]);
  for (const p of b.points) freqs.add(p[0]);
  const sorted = [...freqs].sort((x, y) => x - y);
  return {
    kind: 'fr',
    points: sorted.map((f) => [f, evalFR(a, f) + evalFR(b, f)] as FRPoint),
    interp: 'log-pchip',
    confidence: 'DERIVED',
  };
}

export type MaybeProv<T> = T | Prov<T>;
